import React from 'react';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconLink,
  IconPaperclip,
  IconSend,
} from '@tabler/icons';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Extension } from '@tiptap/core';

interface CaseMessageComposerProps {
  onSend: (body: Record<string, any>, files?: File[]) => void;
  isPending: boolean;
  placeholder?: string;
  initialContent?: Record<string, any>;
}

const CaseMessageComposer: React.FC<CaseMessageComposerProps> = ({
  onSend,
  isPending,
  placeholder = 'Type a message...',
  initialContent,
}) => {
  const sendRef = React.useRef<() => void>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
  const [isEmpty, setIsEmpty] = React.useState(!initialContent);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
      Extension.create({
        name: 'submitShortcut',
        addKeyboardShortcuts() {
          return {
            'Mod-Enter': () => {
              sendRef.current?.();
              return true;
            },
          };
        },
      }),
    ],
    content: initialContent || '',
    onUpdate: ({ editor: e }) => {
      setIsEmpty(e.isEmpty);
    },
  });

  const handleSend = React.useCallback(() => {
    if (!editor || editor.isEmpty || isPending) return;
    const json = editor.getJSON();
    onSend(json, pendingFiles.length > 0 ? pendingFiles : undefined);
    editor.commands.clearContent();
    setPendingFiles([]);
    setIsEmpty(true);
  }, [editor, isPending, onSend, pendingFiles]);

  React.useEffect(() => {
    sendRef.current = handleSend;
  }, [handleSend]);

  const handleLinkToggle = () => {
    if (!editor) return;
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setPendingFiles((prev) => [...prev, ...Array.from(files)]);
    }
    // Reset so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            color={editor?.isActive('bold') ? 'primary' : 'default'}
          >
            <IconBold size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            color={editor?.isActive('italic') ? 'primary' : 'default'}
          >
            <IconItalic size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton
            size="small"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            color={editor?.isActive('underline') ? 'primary' : 'default'}
          >
            <IconUnderline size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link">
          <IconButton
            size="small"
            onClick={handleLinkToggle}
            color={editor?.isActive('link') ? 'primary' : 'default'}
          >
            <IconLink size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Attach files">
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
          >
            <IconPaperclip size={16} />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1,
          minHeight: 80,
          '& .tiptap': {
            outline: 'none',
            minHeight: 60,
            '& p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'text.disabled',
              pointerEvents: 'none',
              height: 0,
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {pendingFiles.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            px: 1.5,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {pendingFiles.map((file, idx) => (
            <Chip
              key={`${file.name}-${idx}`}
              label={file.name}
              size="small"
              onDelete={() => removeFile(idx)}
              variant="outlined"
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 1,
          py: 0.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Tooltip title="Send (Cmd+Enter)">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSend}
              disabled={isEmpty || isPending}
            >
              <IconSend size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default CaseMessageComposer;
